import { NmLoader } from '../../components/NmLoader';
import { isRunningWorkout } from '../../utils/runningWorkoutMetrics';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../../components/NmButton';
import { NmLoadingOverlay } from '../../components/NmLoadingOverlay';
import { NmSegmentControl } from '../../components/NmSegmentControl';
import { computeComparisonDeltas } from '../../utils/comparison/computeComparisonDeltas';
import { buildComparisonInsights } from '../../utils/comparison/comparisonInsights';
import type { ComparisonMetric } from '../../utils/comparison/alignStreams';
import {
  buildMultiLapOverlaySeries,
  toggleMultiLapSelection,
  type MultiLapSelection,
} from '../../utils/comparison/lapOverlaySeries';
import { lapIndexToSegmentRange, type LapRow } from '../../utils/comparison/lapSegments';
import type { ComparisonSessionApi } from './hooks/useComparisonSession';
import type { ComparisonPreviewSegments, ComparisonStreamState } from './hooks/useComparisonStreams';
import { DualStreamChart } from './DualStreamChart';
import { ComparisonSegmentTable } from './ComparisonSegmentTable';
import { ComparisonPdcChart } from './ComparisonPdcChart';
import { ComparisonChartSkeleton } from './ComparisonChartSkeleton';
import { MultiLapOverlayChart } from './MultiLapOverlayChart';
import { SegmentTimelineBar, defaultSegment, SEGMENT_COLORS } from './SegmentTimelineBar';
import { WorkoutSlotCard } from './WorkoutSlotCard';
import type { SegmentRange } from './types';
import { COMPARISON_COLORS } from './types';

interface ComparisonWorkspaceProps {
  sessionApi: ComparisonSessionApi;
  streams: ComparisonStreamState;
  draftSegments: ComparisonPreviewSegments;
  onDraftSegmentsChange: React.Dispatch<React.SetStateAction<ComparisonPreviewSegments>>;
  onPreviewSegmentsChange: React.Dispatch<React.SetStateAction<ComparisonPreviewSegments>>;
  onPickA: () => void;
  onPickB: () => void;
  onSave?: () => void;
}

const METRIC_IDS: ComparisonMetric[] = ['power', 'heart_rate', 'cadence', 'speed', 'altitude'];

function segmentsEqual(a: SegmentRange | null | undefined, b: SegmentRange | null | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.startSec === b.startSec && a.endSec === b.endSec;
}

function CmpPanel({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  return (
    <details className="cmp-panel" open={defaultOpen}>
      <summary>{title}</summary>
      <div className="cmp-panel__body">{children}</div>
    </details>
  );
}

export function ComparisonWorkspace({
  sessionApi,
  streams,
  draftSegments,
  onDraftSegmentsChange,
  onPreviewSegmentsChange,
  onPickA,
  onPickB,
  onSave,
}: ComparisonWorkspaceProps) {
  const { t } = useTranslation();
  const {
    session,
    setViewMode,
    setMetric,
    setAlignment,
    setMode,
    swapWorkouts,
    setWorkoutA,
    setWorkoutB,
    setSegmentA,
    setSegmentB,
    triggerCompare,
  } = sessionApi;
  const { aligned, metricsA, metricsB, streamA, streamB, fullStreamA, fullStreamB, detailA, detailB, loading, error, streamsReady, metricsLoading } = streams;

  const [selectedLapA, setSelectedLapA] = useState<number | null>(null);
  const [selectedLapB, setSelectedLapB] = useState<number | null>(null);
  const [multiSelectedLaps, setMultiSelectedLaps] = useState<MultiLapSelection[]>([]);
  const workoutIdentityRef = useRef<string | null>(null);

  const durA = detailA?.total_time ?? 0;
  const durB = detailB?.total_time ?? durA;

  const runningCadence = detailA != null && isRunningWorkout(detailA) && (!detailB || isRunningWorkout(detailB));
  const metricOptions = useMemo(
    () =>
      METRIC_IDS.map((id) => ({
        value: id,
        label: t(id === 'cadence' && runningCadence ? 'runningMetrics.cadence' : `compare.mobile.metric.${id}`),
      })),
    [t, runningCadence],
  );

  useEffect(() => {
    if (session.viewMode !== 'segments') return;
    if (durA > 0 && !draftSegments.segmentA) {
      onDraftSegmentsChange((d) => ({ ...d, segmentA: defaultSegment(durA) }));
    }
    if (durB > 0 && !draftSegments.segmentB) {
      onDraftSegmentsChange((d) => ({ ...d, segmentB: defaultSegment(durB) }));
    }
  }, [session.viewMode, durA, durB, draftSegments.segmentA, draftSegments.segmentB, onDraftSegmentsChange]);

  useEffect(() => {
    const identity = `${session.workoutA?.id ?? 'none'}:${session.workoutB?.id ?? 'none'}:${session.mode}`;
    if (workoutIdentityRef.current !== null && workoutIdentityRef.current !== identity) {
      setSelectedLapA(null);
      setSelectedLapB(null);
      setMultiSelectedLaps([]);
      onDraftSegmentsChange({ segmentA: null, segmentB: null });
      onPreviewSegmentsChange({ segmentA: null, segmentB: null });
    }
    workoutIdentityRef.current = identity;
  }, [session.workoutA?.id, session.workoutB?.id, session.mode, onDraftSegmentsChange, onPreviewSegmentsChange]);

  useEffect(() => {
    if (session.segmentA) onDraftSegmentsChange((d) => ({ ...d, segmentA: session.segmentA }));
    if (session.segmentB) onDraftSegmentsChange((d) => ({ ...d, segmentB: session.segmentB }));
  }, [session.segmentA, session.segmentB, onDraftSegmentsChange]);

  const updateDraftA = useCallback(
    (seg: SegmentRange) => {
      setSelectedLapA(null);
      onDraftSegmentsChange((d) => ({ ...d, segmentA: seg }));
      onPreviewSegmentsChange((p) => ({ ...p, segmentA: null }));
    },
    [onDraftSegmentsChange, onPreviewSegmentsChange],
  );

  const updateDraftB = useCallback(
    (seg: SegmentRange) => {
      setSelectedLapB(null);
      onDraftSegmentsChange((d) => ({ ...d, segmentB: seg }));
      onPreviewSegmentsChange((p) => ({ ...p, segmentB: null }));
    },
    [onDraftSegmentsChange, onPreviewSegmentsChange],
  );

  const hasPendingSegments =
    !segmentsEqual(draftSegments.segmentA, session.segmentA) ||
    !segmentsEqual(draftSegments.segmentB, session.segmentB);

  const handleApply = useCallback(() => {
    if (session.viewMode === 'segments') {
      if (draftSegments.segmentA) setSegmentA(draftSegments.segmentA);
      if (draftSegments.segmentB) setSegmentB(draftSegments.segmentB);
      onPreviewSegmentsChange({ segmentA: null, segmentB: null });
    }
    triggerCompare();
  }, [session.viewMode, draftSegments.segmentA, draftSegments.segmentB, onPreviewSegmentsChange, setSegmentA, setSegmentB, triggerCompare]);

  const handleSelectLap = useCallback(
    (slot: 'A' | 'B', lapIndex: number, source: 'A' | 'B') => {
      const detail = source === 'A' ? detailA : detailB;
      const duration = source === 'A' ? durA : durB;
      const laps = (detail?.details?.lap_data ?? []) as LapRow[];
      const seg = lapIndexToSegmentRange(laps, lapIndex, duration);
      if (!seg) return;
      setViewMode('segments');
      if (slot === 'A') {
        setSelectedLapA(lapIndex);
        onDraftSegmentsChange((d) => ({ ...d, segmentA: seg }));
      } else {
        setSelectedLapB(lapIndex);
        onDraftSegmentsChange((d) => ({ ...d, segmentB: seg }));
      }
    },
    [detailA, detailB, durA, durB, onDraftSegmentsChange, setViewMode],
  );

  const handleToggleMultiLap = useCallback((source: 'A' | 'B', index: number) => {
    setMultiSelectedLaps((prev) => toggleMultiLapSelection(prev, source, index));
  }, []);

  const lapsA = (detailA?.details?.lap_data ?? []) as LapRow[];
  const lapsB = (detailB?.details?.lap_data ?? []) as LapRow[];

  const multiLapSeries = useMemo(
    () =>
      buildMultiLapOverlaySeries(multiSelectedLaps, fullStreamA, fullStreamB, lapsA, lapsB, durA, durB, session.metric),
    [multiSelectedLaps, fullStreamA, fullStreamB, lapsA, lapsB, durA, durB, session.metric],
  );

  const labelA = session.workoutA?.title ?? t('compare.workoutA');
  const labelB =
    session.mode === 'one-workout-segments' ? t('compare.workoutB') : session.workoutB?.title ?? t('compare.workoutB');

  const deltaRows = computeComparisonDeltas(metricsA, metricsB);
  const insights = buildComparisonInsights(deltaRows, labelA, labelB, t);

  const ready =
    session.workoutA && (session.mode === 'one-workout-segments' || session.workoutB) && streamsReady;

  return (
    <div style={{ position: 'relative' }}>
      {loading && !streamsReady && (
        <NmLoadingOverlay variant="inline" message={t('compare.mobile.loadingStreams')} />
      )}

      <div className="cmp-slots">
        <WorkoutSlotCard slot="A" workout={session.workoutA} onPick={onPickA} onClear={() => setWorkoutA(null)} />
        {session.mode === 'two-workouts' && (
          <>
            <div className="cmp-swap">
              <NmButton variant="ghost" size="sm" onClick={swapWorkouts} aria-label={t('compare.mobile.swap')}>
                ⇄
              </NmButton>
            </div>
            <WorkoutSlotCard slot="B" workout={session.workoutB} onPick={onPickB} onClear={() => setWorkoutB(null)} />
          </>
        )}
      </div>

      <section className="cmp-chart-studio" aria-labelledby="cmp-chart-studio-title">
        <header className="cmp-chart-studio__header">
          <div>
            <h3 id="cmp-chart-studio-title" className="cmp-chart-studio__title">{t('compare.chart.title')}</h3>
            <p className="cmp-chart-studio__hint">{t('compare.chart.hint')}</p>
          </div>
        </header>

        <div className="cmp-chart-studio__controls">
          <div className="cmp-chart-studio__control">
            <span>{t('compare.mobile.modeLabel')}</span>
            <NmSegmentControl
              className="cmp-chart-studio__mode-segment"
              variant="track"
              accent
              value={session.mode}
              options={[
                { value: 'two-workouts', label: t('compare.workspace.twoWorkouts') },
                { value: 'one-workout-segments', label: t('compare.workspace.oneWorkoutSegments') },
              ]}
              onChange={setMode}
            />
          </div>
          {session.mode === 'two-workouts' && (
            <div className="cmp-chart-studio__control">
              <span>{t('compare.mobile.rangeLabel')}</span>
              <NmSegmentControl
                variant="track"
                accent
                value={session.viewMode}
                options={[
                  { value: 'full', label: t('compare.workspace.fullWorkout') },
                  { value: 'segments', label: t('compare.workspace.segments') },
                ]}
                onChange={setViewMode}
              />
            </div>
          )}
          <div className="cmp-chart-studio__control">
            <span>{t('compare.mobile.alignLabel')}</span>
            <NmSegmentControl
              variant="track"
              accent
              value={session.alignment}
              options={[
                { value: 'time', label: t('compare.alignTime') },
                { value: 'distance', label: t('compare.alignDistance') },
              ]}
              onChange={setAlignment}
            />
          </div>
        </div>

        {ready && (
          <div className="cmp-chart-studio__metric">
            <span className="cmp-chart-studio__metric-label">{t('compare.chart.metricLabel')}</span>
            <div className="cmp-metric-scroll">
              <NmSegmentControl variant="track" accent value={session.metric} options={metricOptions} onChange={setMetric} />
            </div>
          </div>
        )}

        {session.viewMode === 'segments' && ready && (
          <div className="cmp-chart-studio__segments">
            <div className="cmp-chart-studio__segment-bars">
              <SegmentTimelineBar
                label={session.mode === 'one-workout-segments' ? t('compare.workspace.segmentA') : t('compare.workspace.workoutARange')}
                color={SEGMENT_COLORS.a}
                durationSec={durA}
                segment={draftSegments.segmentA}
                onPreviewChange={(seg) => onPreviewSegmentsChange((p) => ({ ...p, segmentA: seg }))}
                onDraftChange={updateDraftA}
              />
              <SegmentTimelineBar
                label={session.mode === 'one-workout-segments' ? t('compare.workspace.segmentB') : t('compare.workspace.workoutBRange')}
                color={SEGMENT_COLORS.b}
                durationSec={durB}
                segment={draftSegments.segmentB}
                onPreviewChange={(seg) => onPreviewSegmentsChange((p) => ({ ...p, segmentB: seg }))}
                onDraftChange={updateDraftB}
              />
            </div>
            <div className="cmp-chart-studio__segment-actions">
              <NmButton
                variant="primary"
                size="sm"
                onClick={handleApply}
                loading={metricsLoading}
                disabled={metricsLoading || !draftSegments.segmentA || !draftSegments.segmentB}
              >
                {metricsLoading ? t('compare.workspace.loading') : t('compare.workspace.apply')}
              </NmButton>
              {hasPendingSegments && <span>{t('compare.workspace.unappliedSegments')}</span>}
            </div>
          </div>
        )}

        {error && <p className="cmp-chart-studio__error">{error}</p>}
        {!ready && <div className="cmp-chart-studio__empty">{t('compare.chart.pickHint')}</div>}

        {ready && (
          <div className="cmp-charts-stack">
            {multiLapSeries.length >= 2 && (
              <div className="cmp-chart-wrap">
                <MultiLapOverlayChart running={runningCadence} series={multiLapSeries} metric={session.metric} />
              </div>
            )}

            <div className="cmp-chart-wrap" style={{ position: 'relative' }}>
              <DualStreamChart running={runningCadence}
                aligned={aligned}
                metric={session.metric}
                alignment={session.alignment}
                labelA={labelA}
                labelB={labelB}
                height={220}
              />
              {metricsLoading && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
                  <ComparisonChartSkeleton />
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {ready && (
        <>

          <div className="cmp-metrics-compact">
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-1)', marginBottom: 4 }}>{t('compare.metrics.title')}</div>
            {metricsLoading ? (
              <p style={{ fontSize: 12, color: 'var(--fg-3)' }}><NmLoader size="sm" inline label={t('compare.metrics.loading')} /></p>
            ) : deltaRows.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--fg-3)' }}>{t('compare.metrics.noMetrics')}</p>
            ) : (
              <>
                <div className="cmp-metric-row cmp-metric-row--header">
                  <span aria-hidden="true" />
                  <span className="cmp-metric-row__athlete" style={{ color: COMPARISON_COLORS.a }} title={labelA}>{labelA}</span>
                  <span className="cmp-metric-row__athlete" style={{ color: COMPARISON_COLORS.b }} title={labelB}>{labelB}</span>
                  <span className="cmp-metric-row__delta-heading">Δ</span>
                </div>
                {deltaRows.map((r) => {
                  const positive = r.delta != null && (r.higherIsBetter ? r.delta > 0 : r.delta < 0);
                  const negative = r.delta != null && (r.higherIsBetter ? r.delta < 0 : r.delta > 0);
                  return (
                    <div key={r.key} className="cmp-metric-row">
                      <span className="cmp-metric-row__label">{r.label}</span>
                      <span className="cmp-metric-row__val" style={{ color: COMPARISON_COLORS.a }}>{r.valueAFormatted}</span>
                      <span className="cmp-metric-row__val" style={{ color: COMPARISON_COLORS.b }}>{r.valueBFormatted}</span>
                      <span
                        className="cmp-metric-row__delta"
                        style={{ color: positive ? '#16a34a' : negative ? '#f97316' : 'var(--fg-3)' }}
                      >
                        {r.deltaFormatted}
                      </span>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {session.mode === 'two-workouts' && session.workoutA && session.workoutB && (
            <CmpPanel title={t('compare.meanMaxCurve')}>
              <ComparisonPdcChart
                detailA={detailA}
                detailB={detailB}
                workoutA={session.workoutA}
                workoutB={session.workoutB}
              />
            </CmpPanel>
          )}

          {insights.length > 0 && (
            <CmpPanel title={t('compare.insights')}>
              <ul className="cmp-insights-list">
                {insights.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </CmpPanel>
          )}

          <CmpPanel title={t('compare.segmentTable.lapComparison')}>
            <ComparisonSegmentTable
              hideTitle
              detailA={detailA}
              detailB={detailB}
              mode={session.mode}
              viewMode={session.viewMode}
              segmentA={draftSegments.segmentA}
              segmentB={draftSegments.segmentB}
              selectedLapA={selectedLapA}
              selectedLapB={selectedLapB}
              multiSelectedLaps={multiSelectedLaps}
              onSelectLap={handleSelectLap}
              onToggleMultiLap={handleToggleMultiLap}
              onClearMultiLaps={() => setMultiSelectedLaps([])}
            />
          </CmpPanel>

        </>
      )}

      <div className="cmp-save-comparison">
        <NmButton
          className="cmp-save-comparison__button"
          variant="secondary"
          size="sm"
          onClick={() => onSave?.()}
          disabled={!ready || !onSave}
        >
          {t('compare.savePrompt')}
        </NmButton>
      </div>
    </div>
  );
}
